// Searchable IANA zone picker (architecture sprint-04 §6.4). Intl.supportedValuesOf when available,
// else the saved zone plus the browser zone; the saved zone is always listed.
import { isValidTimeZone, zoneOffsetMinutes, type IanaZone } from '@shared/calendar';
import { ChevronsUpDown } from 'lucide-react';
import { useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/cn';

const MAX_MATCHES = 50;
const MINUTES_PER_HOUR = 60;

export function browserZone(): IanaZone | null {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz && isValidTimeZone(tz) ? tz : null;
  } catch {
    return null;
  }
}

export function availableZones(saved: IanaZone): IanaZone[] {
  const intl = Intl as unknown as { supportedValuesOf?: (key: 'timeZone') => string[] };
  let zones: string[] = [];
  if (typeof intl.supportedValuesOf === 'function') {
    try {
      zones = intl.supportedValuesOf('timeZone');
    } catch {
      zones = [];
    }
  }
  const browser = browserZone();
  const extra = [saved, ...(zones.length === 0 && browser ? [browser] : [])];
  return [...new Set([...zones, ...extra])].sort();
}

/** 'Asia/Colombo (UTC+05:30)'. */
export function zoneLabel(tz: IanaZone, at: Date = new Date()): string {
  const offset = zoneOffsetMinutes(tz, at);
  const sign = offset < 0 ? '-' : '+';
  const abs = Math.abs(offset);
  const hh = String(Math.floor(abs / MINUTES_PER_HOUR)).padStart(2, '0');
  const mm = String(abs % MINUTES_PER_HOUR).padStart(2, '0');
  return `${tz} (UTC${sign}${hh}:${mm})`;
}

function normalise(text: string): string {
  return text.toLowerCase().replace(/[/_]/g, ' ');
}

export function filterZones(zones: IanaZone[], query: string): IanaZone[] {
  const q = normalise(query.trim());
  const matches = q === '' ? zones : zones.filter((z) => normalise(z).includes(q));
  return matches.slice(0, MAX_MATCHES);
}

interface TimezonePickerProps {
  id?: string;
  value: IanaZone;
  onChange: (tz: IanaZone) => void;
  disabled?: boolean;
  'aria-labelledby'?: string;
}

export function TimezonePicker({ id, value, onChange, disabled, 'aria-labelledby': labelledBy }: TimezonePickerProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const listId = useId();
  const listRef = useRef<HTMLUListElement>(null);
  const zones = useMemo(() => availableZones(value), [value]);
  const matches = useMemo(() => filterZones(zones, query), [zones, query]);

  function choose(tz: IanaZone): void {
    setOpen(false);
    setQuery('');
    if (tz !== value) onChange(tz);
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlight((h) => Math.min(h + 1, matches.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const tz = matches[highlight];
      if (tz) choose(tz);
    }
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          setQuery('');
          setHighlight(0);
        }
      }}
    >
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-labelledby={labelledBy}
          disabled={disabled}
          className="flex w-full items-center justify-between gap-2 rounded-lg border border-input bg-field px-3 py-2 text-left text-base leading-6 text-foreground focus-visible:outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 sm:text-sm sm:leading-5"
        >
          <span id={id ? `${id}-value` : undefined} className="truncate">
            {zoneLabel(value)}
          </span>
          <ChevronsUpDown aria-hidden="true" className="h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-72 p-2" align="start">
        <Input
          autoFocus
          aria-label="Search timezones"
          aria-controls={listId}
          placeholder="Search, e.g. Colombo or New York"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setHighlight(0);
          }}
          onKeyDown={onKeyDown}
        />
        <ul ref={listRef} id={listId} role="listbox" aria-label="Timezones" className="mt-2 max-h-64 overflow-y-auto">
          {matches.length === 0 && <li className="px-2 py-1.5 text-xs text-muted-foreground">No matching timezone</li>}
          {matches.map((tz, i) => (
            <li
              key={tz}
              role="option"
              aria-selected={tz === value}
              onMouseEnter={() => setHighlight(i)}
              onClick={() => choose(tz)}
              className={cn('flex cursor-default select-none items-center gap-2 rounded px-2 py-1.5 text-sm text-muted-foreground', i === highlight && 'bg-accent text-foreground')}
            >
              {zoneLabel(tz)}
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
